import { StreamManifest } from 'content/library/types/contentVideoType';
import { VideoThumbnailType } from './videoThumbnailType';

interface ContentType {
  active: boolean;
  age_rating: string;
  content_type: string;
  duration: number | null;
  episode_order: number | null;
  episode_part: number | null;
  id: number;
  release_date_end: string | null;
  release_date_start: string | null;
  season_id: number | null;
  slug: string;
  status: string;
  synopsis: string | null;
  synopsis_en: string | null;
  title: string;
  title_en: string | null;
}

export interface VideoHistory {
  id: number;
  video: number;
  user: string;
  action: string;
  status: string;
  extra_info: Record<string, any>;
  created?: string; // 생성 시간 (선택적 필드로 추가)
}

export interface VideoType {
  id: number;
  created?: string;
  file_name: string;
  file_size?: number;
  file_type?: string;
  file?: string;
  external_url?: string;
  file_url?: string;
  stream_manifests?: StreamManifest[];
  video_histories?: VideoHistory[];
  video_thumbnails?: VideoThumbnailType[];
  ingest_source?: string;
  duration?: number;
  status?: string;
  stream_manifest_status?: string;
  contents?: ContentType[];
}

/**
 * Check if a video asset has a valid playable video URL.
 * Returns true if:
 * 1. Video status is "completed" AND (file_url or external_url exists), OR
 * 2. stream_manifests has an entry with preset "1080p" or "720p" AND status "completed"
 */
export const hasPlayableVideoUrl = (video: VideoType | null | undefined): boolean => {
  if (!video) return false;

  // Check original condition: status completed + file_url or external_url
  const hasDirectUrl =
    video.status?.toLowerCase() === 'completed' && (video.file_url || video.external_url);

  if (hasDirectUrl) return true;

  // Check stream_manifests for playable streams (1080p or 720p with completed status)
  if (video.stream_manifests && video.stream_manifests.length > 0) {
    const hasPlayableStream = video.stream_manifests.some(
      manifest =>
        (manifest.preset === '1080p' || manifest.preset === '720p') &&
        manifest.status?.toLowerCase() === 'completed'
    );
    if (hasPlayableStream) return true;
  }

  return false;
};

/**
 * Get the best playable video URL from a video asset.
 * Priority:
 * 1. file_url (if status is completed)
 * 2. external_url (if status is completed)
 * 3. stream_manifests with 1080p preset (if status is completed)
 * 4. stream_manifests with 720p preset (if status is completed)
 */
export const getPlayableVideoUrl = (video: VideoType | null | undefined): string | undefined => {
  if (!video) return undefined;

  // Check if direct URL is available (original logic)
  if (video.status?.toLowerCase() === 'completed') {
    if (video.file_url) return video.file_url;
    if (video.external_url) return video.external_url;
  }

  // Check stream_manifests for playable streams
  if (video.stream_manifests && video.stream_manifests.length > 0) {
    // Prefer 1080p, then 720p
    const manifest1080p = video.stream_manifests.find(
      m => m.preset === '1080p' && m.status?.toLowerCase() === 'completed'
    );
    if (manifest1080p?.stream_url_full) return manifest1080p.stream_url_full;

    const manifest720p = video.stream_manifests.find(
      m => m.preset === '720p' && m.status?.toLowerCase() === 'completed'
    );
    if (manifest720p?.stream_url_full) return manifest720p.stream_url_full;
  }

  return undefined;
};
